import React from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router-dom';
import { Col, Container, FormFeedback, Input, Row } from 'reactstrap';

import AppInputValidation from 'components/App/InputValidation';
import AppAsyncSelectValidation from 'components/App/AppAsyncSelectValidation';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import CardImage from 'components/App/CardImage';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';

import voucherCampaignApi from 'api/voucherCampaignApi';
import { debounce } from 'lodash';

import { LIMIT_FILE_SIZE, themeColor } from 'constants/index';
import ENDPOINT from 'constants/endPoint';
import INITIAL_VALUE from 'constants/initialValue';
import formatUploadImages from 'utils/formatUploadImages';
import randomIDString from 'utils/randomId';
import {
  getSanitizedInteger,
  onKeyDownIntegerOnly,
  convertIsoDateToString,
} from 'utils';

import { ReactComponent as AddPictureIcon } from 'assets/images/add-picture.svg';
import { ReactComponent as GiftIcon } from 'assets/images/Gift-SVG.svg';
import { ReactComponent as PlusIcon } from 'assets/images/plus.svg';

import buildGiftCreatePayload from './buildGiftCreatePayload';
import giftCreateValidationSchema, {
  GIFT_NAME_MAX_LENGTH,
} from './giftCreateValidation';
import styles from './ManagerGiftCreate.module.scss';

const GIFT_CREATE_TOUCHED_FIELDS = {
  name: true,
  deliveryMethod: true,
  stockQuantity: true,
  perAccountRedeemLimit: true,
  redeemPoints: true,
  images: true,
  limitStartAt: true,
  limitEndAt: true,
  voucherCampaign: true,
};

const GIFT_IMAGE_THUMB_STYLE = {
  aspectRatio: 1,
  width: 180,
  height: 167,
};

const OPTION_UPLOAD_IMAGES = {
  limitValue: 5,
  fileSize: LIMIT_FILE_SIZE.LARGE,
};

function GiftImageDragPreview({ item, style }) {
  return (
    <div className={styles.DragPreview} style={style}>
      <img src={item.image} alt="preview" />
    </div>
  );
}

GiftImageDragPreview.propTypes = {
  item: PropTypes.shape({
    image: PropTypes.string.isRequired,
  }).isRequired,
  style: PropTypes.instanceOf(Object),
};

GiftImageDragPreview.defaultProps = {
  style: GIFT_IMAGE_THUMB_STYLE,
};

const PAGE_TITLE = 'Thêm quà mới';

const GIFT_TYPE = {
  EXCLUSIVE: 'EXCLUSIVE',
  VOUCHER: 'VOUCHER',
  LIMITED: 'LIMITED',
};

const GIFT_TYPE_OPTIONS = [
  {
    value: GIFT_TYPE.EXCLUSIVE,
    label: 'Quà Độc Quyền',
    title: 'Quà Độc Quyền',
  },
  {
    value: GIFT_TYPE.VOUCHER,
    label: 'Quà Voucher',
    title: 'Quà Voucher',
  },
];

const GIFT_STATUS = {
  HIDDEN: 'HIDDEN',
  PUBLIC: 'PUBLIC',
};

const DELIVERY_METHOD = {
  EMAIL: 'EMAIL',
  BOOKESE_SYSTEM: 'BOOKESE_SYSTEM',
  EXPRESS: 'EXPRESS',
};

const DELIVERY_METHOD_OPTIONS = [
  {
    value: DELIVERY_METHOD.EMAIL,
    label: 'Gửi qua Email',
  },
  // {
  //   value: DELIVERY_METHOD.BOOKESE_SYSTEM,
  //   label: 'Nhận trên hệ thống Bookese',
  // },
  {
    value: DELIVERY_METHOD.EXPRESS,
    label: 'Giao hàng',
  },
];

const GIFT_DETAIL_MAX_LENGTH = 1000;

const GIFT_DETAIL_FIELDS = [
  {
    name: 'shortDescription',
    label: 'Mô tả ngắn',
    placeholder: 'Nhập các gạch đầu dòng tóm tắt quyền lợi của voucher.',
  },
  {
    name: 'howToUse',
    label: 'Cách sử dụng',
    placeholder: 'Nhập chi tiết các bước hướng dẫn sử dụng.',
  },
  {
    name: 'termsOfUse',
    label: 'Điều kiện áp dụng',
    placeholder: 'Nhập chi tiết về khu vực, thời gian và các quy định áp dụng.',
  },
];

const breadcrumbList = [
  {
    name: 'Quà tặng',
    isActive: false,
    link: ENDPOINT.MANAGER_GIFT_LIST,
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

function ManagerGiftCreate({
  isLoadingUploadMultipleMedia,
  isCreateGiftLoading,
  actionUploadMultiMedia,
  actionCreateGift,
}) {
  const history = useHistory();

  const [activeImageId, setActiveImageId] = React.useState(null);
  const inputFileRef = React.useRef(null);

  const validation = useFormik({
    validationSchema: giftCreateValidationSchema,
    initialValues: {
      name: '',
      giftType: GIFT_TYPE.VOUCHER,
      deliveryMethod: '',
      isLimited: false,
      voucherCampaign: '',
      limitStartAt: '',
      limitEndAt: '',
      status: GIFT_STATUS.PUBLIC,
      images: [],
      redeemPoints: '',
      stockQuantity: 0,
      expiryUsage: '',
      perAccountRedeemLimit: 0,
      shortDescription: '',
      howToUse: '',
      termsOfUse: '',
      returnPolicy: '',
    },
    onSubmit: (values) => {
      const payload = buildGiftCreatePayload(values);
      actionCreateGift({
        payload,
        callback: () => {
          history.push(ENDPOINT.MANAGER_GIFT_LIST);
        },
      });
    },
  });

  const getFieldError = React.useCallback(
    (field) => {
      if (validation.submitCount === 0 && !validation.touched[field]) {
        return '';
      }

      return validation.errors[field] || '';
    },
    [validation.errors, validation.submitCount, validation.touched],
  );

  const onSaveGift = React.useCallback(async () => {
    const errors = await validation.validateForm();
    validation.setTouched(GIFT_CREATE_TOUCHED_FIELDS);

    if (Object.keys(errors).length === 0) {
      validation.submitForm();
    }
  }, [validation]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const activeDragImage = React.useMemo(
    () => validation.values.images.find((item) => item.id === activeImageId),
    [activeImageId, validation.values.images],
  );

  const onUploadMultiImages = React.useCallback(
    (imageList) => {
      const imageUploadList = formatUploadImages(
        imageList,
        OPTION_UPLOAD_IMAGES,
      );

      if (imageUploadList.length > 0) {
        actionUploadMultiMedia({
          images: imageUploadList,
          callback: (uploadedUrls) => {
            const newImages = uploadedUrls.map((image) => ({
              image,
              id: randomIDString(),
              checked: false,
            }));

            validation.setFieldValue(
              'images',
              [...validation.values.images, ...newImages],
              true,
            );
            validation.setFieldTouched('images', true, false);

            if (inputFileRef.current) {
              inputFileRef.current.value = '';
            }
          },
        });
      }
    },
    [actionUploadMultiMedia, validation],
  );

  const onChangeAddImages = React.useCallback(
    (e) => {
      onUploadMultiImages(e.target.files);
    },
    [onUploadMultiImages],
  );

  const onDragStart = React.useCallback((event) => {
    setActiveImageId(event.active.id);
  }, []);

  const onDragCancel = React.useCallback(() => {
    setActiveImageId(null);
  }, []);

  const onDragImage = React.useCallback(
    (event) => {
      const { active, over } = event;

      setActiveImageId(null);

      if (!over || active.id === over.id) {
        return;
      }

      validation.setValues(({ images }) => {
        const oldIndex = images.findIndex(({ id }) => id === active.id);
        const newIndex = images.findIndex(({ id }) => id === over.id);

        if (oldIndex < 0 || newIndex < 0) {
          return { images };
        }

        return { images: arrayMove(images, oldIndex, newIndex) };
      });
    },
    [validation],
  );

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: { 'image/*': [] },
    multiple: true,
    onDrop: (files) => onUploadMultiImages(files),
  });

  const onClickOpenFileInput = React.useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  const onSelectImage = React.useCallback(
    (e) => {
      const { name } = e.target;

      validation.setFieldValue(
        'images',
        validation.values.images.map((item) =>
          (item.id === name ? { ...item, checked: !item.checked } : item),
        ),
        true,
      );
    },
    [validation],
  );

  const onSelectAllImages = React.useCallback(() => {
    validation.setFieldValue(
      'images',
      validation.values.images.map((item) => ({ ...item, checked: true })),
      true,
    );
  }, [validation]);

  const onClearSelectAllImages = React.useCallback(() => {
    validation.setFieldValue(
      'images',
      validation.values.images.map((item) => ({ ...item, checked: false })),
      true,
    );
  }, [validation]);

  const onDeleteSelectedImages = React.useCallback(() => {
    validation.setFieldValue(
      'images',
      validation.values.images.filter((item) => !item.checked),
      true,
    );
  }, [validation]);

  const giftTypeTitle = React.useMemo(() => {
    const selected = GIFT_TYPE_OPTIONS.find(
      (item) => item.value === validation.values.giftType,
    );
    return selected?.title || GIFT_TYPE_OPTIONS[0].title;
  }, [validation.values.giftType]);

  const isLimitedGift = validation.values.isLimited;
  const isExclusiveGift = validation.values.giftType === GIFT_TYPE.EXCLUSIVE;
  const isVoucherGift = validation.values.giftType === GIFT_TYPE.VOUCHER;

  const onChangeGiftType = React.useCallback(
    (e) => {
      const nextGiftType = e.target.value;

      if (nextGiftType === GIFT_TYPE.EXCLUSIVE) {
        validation.setValues(
          {
            ...validation.values,
            giftType: nextGiftType,
            deliveryMethod: DELIVERY_METHOD.EXPRESS,
            voucherCampaign: '',
          },
          true,
        );
      } else {
        validation.setFieldValue('giftType', nextGiftType, true);
      }
    },
    [validation],
  );

  const [voucherCampaignOptions, setVoucherCampaignOptions] = React.useState(
    [],
  );
  const [isLoadingVoucherCampaigns, setIsLoadingVoucherCampaigns] =
    React.useState(false);
  const [selectedCampaignDetails, setSelectedCampaignDetails] =
    React.useState(null);

  const fetchDefaultVoucherCampaigns = React.useCallback(() => {
    setIsLoadingVoucherCampaigns(true);
    voucherCampaignApi
      .getListVoucherCampaign({
        unlinkedToGift: true,
        limit: 10,
      })
      .then((response) => {
        const data = response.payload?.data || response.data || response || [];
        const formatted = data.map((item) => ({
          label: item.name,
          value: item._id,
        }));
        setVoucherCampaignOptions(formatted);
      })
      .finally(() => {
        setIsLoadingVoucherCampaigns(false);
      });
  }, []);

  const fetchVoucherCampaignDetail = React.useCallback((campaignId) => {
    if (!campaignId) {
      setSelectedCampaignDetails(null);
      return;
    }
    voucherCampaignApi
      .getVoucherCampaignDetail({ id: campaignId })
      .then((response) => {
        const detail = response?.payload || response?.data || response;
        setSelectedCampaignDetails(detail);
      })
      .catch(() => {
        setSelectedCampaignDetails(null);
      });
  }, []);

  React.useEffect(() => {
    if (validation.values.voucherCampaign) {
      fetchVoucherCampaignDetail(validation.values.voucherCampaign);
    } else {
      setSelectedCampaignDetails(null);
    }
  }, [validation.values.voucherCampaign, fetchVoucherCampaignDetail]);

  React.useEffect(() => {
    if (validation.values.giftType === GIFT_TYPE.VOUCHER) {
      fetchDefaultVoucherCampaigns();
    }
  }, [validation.values.giftType, fetchDefaultVoucherCampaigns]);

  const onSearchVoucherCampaign = React.useMemo(
    () =>
      debounce((inputValue, callback) => {
        voucherCampaignApi
          .getListVoucherCampaign({
            q: inputValue,
            unlinkedToGift: true,
            limit: 10,
          })
          .then((response) => {
            const data =
              response.payload?.data || response.data || response || [];
            const formatted = data.map((item) => ({
              label: item.name,
              value: item._id,
            }));
            setVoucherCampaignOptions(formatted);
            callback(formatted);
          })
          .catch(() => {
            callback([]);
          });
      }, 500),
    [],
  );

  const onSelectVoucherCampaign = React.useCallback(
    (e) => {
      const campaignId = e?.value || '';
      validation.setFieldValue('voucherCampaign', campaignId, true);
      validation.setFieldTouched('voucherCampaign', true, false);
      fetchVoucherCampaignDetail(campaignId);
    },
    [validation, fetchVoucherCampaignDetail],
  );

  const onMenuCloseVoucherCampaign = React.useCallback(() => {
    fetchDefaultVoucherCampaigns();
  }, [fetchDefaultVoucherCampaigns]);

  const onChangeIsLimited = React.useCallback(
    (e) => {
      const isChecked = e.target.checked;
      validation.setFieldValue('isLimited', isChecked);

      if (!isChecked) {
        validation.setFieldValue('limitStartAt', '', true);
        validation.setFieldValue('limitEndAt', '', true);
        validation.setFieldTouched('limitStartAt', false, false);
        validation.setFieldTouched('limitEndAt', false, false);
      }
    },
    [validation],
  );

  const isDeliveryOptionDisabled = React.useCallback(
    (value) => isExclusiveGift && value !== DELIVERY_METHOD.EXPRESS,
    [isExclusiveGift],
  );

  const onChangeDeliveryMethod = React.useCallback(
    (e) => {
      validation.setFieldValue('deliveryMethod', e.target.value, true);
      validation.setFieldTouched('deliveryMethod', true, false);
    },
    [validation],
  );

  const onChangeStatus = React.useCallback(
    (e) => {
      validation.setFieldValue('status', e.target.value);
    },
    [validation],
  );

  const onChangeLimitStartDate = React.useCallback(
    (value) => {
      validation.setFieldValue('limitStartAt', value ? value.toDate() : '', true);
      validation.setFieldTouched('limitStartAt', true, false);
    },
    [validation],
  );

  const onChangeLimitEndDate = React.useCallback(
    (value) => {
      validation.setFieldValue('limitEndAt', value ? value.toDate() : '', true);
      validation.setFieldTouched('limitEndAt', true, false);
    },
    [validation],
  );

  const toDatePickerValue = React.useCallback((value) => {
    if (!value) return undefined;
    return moment(value).isValid() ? dayjs(value) : undefined;
  }, []);

  const disabledStartDate = React.useCallback((current) => {
    if (!current) return false;
    return current.valueOf() < dayjs().startOf('day').valueOf();
  }, []);

  const disabledEndDate = React.useCallback(
    (current) => {
      if (!current) return false;
      const startLimit = validation.values.limitStartAt;
      const baseDate = startLimit ? dayjs(startLimit) : dayjs();
      return current.valueOf() < baseDate.startOf('day').valueOf();
    },
    [validation.values.limitStartAt],
  );

  const onAdjustStock = React.useCallback(
    (delta) => () => {
      validation.setFieldValue(
        'stockQuantity',
        Math.min(
          999,
          Math.max(0, (Number(validation.values.stockQuantity) || 0) + delta),
        ),
        true,
      );
      validation.setFieldTouched('stockQuantity', true, false);
    },
    [validation],
  );

  const onAdjustPerAccountLimit = React.useCallback(
    (delta) => () => {
      validation.setFieldValue(
        'perAccountRedeemLimit',
        Math.min(
          999,
          Math.max(
            0,
            (Number(validation.values.perAccountRedeemLimit) || 0) + delta,
          ),
        ),
        true,
      );
      validation.setFieldTouched('perAccountRedeemLimit', true, false);
    },
    [validation],
  );

  const onChangeStockQuantity = React.useCallback(
    (e) => {
      validation.setFieldValue(
        'stockQuantity',
        getSanitizedInteger(e.target.value),
        true,
      );
    },
    [validation],
  );

  const onChangePerAccountLimit = React.useCallback(
    (e) => {
      validation.setFieldValue(
        'perAccountRedeemLimit',
        getSanitizedInteger(e.target.value),
        true,
      );
    },
    [validation],
  );

  const onCancel = React.useCallback(() => {
    history.push(ENDPOINT.MANAGER_GIFT_LIST);
  }, [history]);

  return (
    <div className="page-content">
      <Meta name={PAGE_TITLE} />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <div className={styles.Page}>
          <div className={styles.Wrapper}>
            {/* info gift */}
            <Card className={styles.Section}>
              <div className={styles.SectionTop}>
                <GiftIcon className={styles.Icon} />

                <div className={styles.SectionTopTitle}>
                  <h3>{giftTypeTitle}</h3>
                  <p>
                    Tùy chỉnh khuyến mãi này theo nhu cầu của Quý vị. Chọn ngày,
                    mức giảm giá, đối tượng áp dụng và hơn thế nữa.
                  </p>
                </div>
              </div>
            </Card>
            {/* basic info */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>Thông tin cơ bản</h3>

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Tên quà tặng</p>
                <AppInputValidation
                  inputClassName={styles.FormFieldInput}
                  name="name"
                  keyName="name"
                  placeholder="Nhập tên quà tặng (Ví dụ: eVoucher giảm 15% Grab đưa đón sân bay)"
                  validation={validation}
                  onBlur={validation.handleBlur}
                  limitCharacter={GIFT_NAME_MAX_LENGTH}
                  isShowCounter
                />
              </div>

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Hình thức vận chuyển</p>
                <div className={styles.OptionGroup}>
                  {DELIVERY_METHOD_OPTIONS.map(({ value, label }) => {
                    const isDisabled = isDeliveryOptionDisabled(value);

                    return (
                      <label
                        key={value}
                        htmlFor={`delivery-${value}`}
                        className={classNames(
                          isDisabled && styles.OptionDisabled,
                        )}
                      >
                        <Input
                          id={`delivery-${value}`}
                          type="radio"
                          name="deliveryMethod"
                          value={value}
                          checked={validation.values.deliveryMethod === value}
                          disabled={isDisabled}
                          onChange={onChangeDeliveryMethod}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
                {getFieldError('deliveryMethod') && (
                  <FormFeedback className="d-block">
                    {getFieldError('deliveryMethod')}
                  </FormFeedback>
                )}
              </div>

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Loại quà</p>
                <div className={styles.OptionGroup}>
                  {GIFT_TYPE_OPTIONS.map(({ value, label }) => (
                    <label key={value} htmlFor={`gift-type-${value}`}>
                      <Input
                        id={`gift-type-${value}`}
                        type="radio"
                        name="giftType"
                        value={value}
                        checked={validation.values.giftType === value}
                        onChange={onChangeGiftType}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {isVoucherGift && (
                <div className={styles.FormField}>
                  <div className={styles.FormFieldTitleRow}>
                    <p className={styles.FormFieldTitle}>Chiến dịch voucher</p>
                    {validation.values.voucherCampaign && (
                      <button
                        type="button"
                        className="button-action button-link"
                        onClick={() => history.push(
                          ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(
                            validation.values.voucherCampaign,
                          ),
                        )}
                      >
                        Xem chi tiết
                      </button>
                    )}
                  </div>
                  <AppAsyncSelectValidation
                    name="voucherCampaign"
                    placeholder="Chọn chiến dịch voucher"
                    onChange={onSelectVoucherCampaign}
                    defaultOptions={voucherCampaignOptions}
                    options={voucherCampaignOptions}
                    value={
                      voucherCampaignOptions.find(
                        (o) => o.value === validation.values.voucherCampaign,
                      ) || null
                    }
                    validation={validation}
                    isUsePureValue
                    isLoading={isLoadingVoucherCampaigns}
                    onSearch={onSearchVoucherCampaign}
                    isHiddenSearchable
                    onMenuClose={onMenuCloseVoucherCampaign}
                  />
                </div>
              )}

              {isVoucherGift && selectedCampaignDetails && (
                <div className="w-100">
                  <Row className={styles.ValueGrid}>
                    <Col md={4} className={styles.ValueCol}>
                      <div className={styles.FormField}>
                        <p className={styles.FormFieldTitle}>Ngày bắt đầu</p>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails.startAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.FormFieldInput}
                        />
                      </div>
                    </Col>
                    <Col md={4} className={styles.ValueCol}>
                      <div className={styles.FormField}>
                        <p className={styles.FormFieldTitle}>Ngày kết thúc</p>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails.endAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.FormFieldInput}
                        />
                      </div>
                    </Col>
                    <Col md={4} className={styles.ValueCol}>
                      <div className={styles.FormField}>
                        <p className={styles.FormFieldTitle}>Ngày hết hạn</p>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails.voucherExpiryAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.FormFieldInput}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Thuộc tính</p>
                <div className={styles.OptionGroup}>
                  <label htmlFor="gift-is-limited">
                    <Input
                      id="gift-is-limited"
                      type="checkbox"
                      name="isLimited"
                      checked={validation.values.isLimited}
                      onChange={onChangeIsLimited}
                    />
                    <span>Quà giới hạn</span>
                  </label>
                </div>
              </div>

              {isLimitedGift && (
                <div className={styles.FormField}>
                  <p className={styles.FormFieldTitle}>Giới hạn thời gian</p>
                  <Row className={styles.DateRow}>
                    <Col md={5} className={styles.DateCol}>
                      <DatePicker
                        id="limitStartAt"
                        className={styles.CampaignDatePicker}
                        showTime={{ format: 'HH:mm' }}
                        format="DD/MM/YYYY HH:mm"
                        value={toDatePickerValue(validation.values.limitStartAt)}
                        onChange={onChangeLimitStartDate}
                        needConfirm={false}
                        inputReadOnly
                        placeholder="Bắt đầu vào"
                        disabledDate={disabledStartDate}
                      />
                      {getFieldError('limitStartAt') && (
                        <FormFeedback className="d-block mt-2">
                          {getFieldError('limitStartAt')}
                        </FormFeedback>
                      )}
                    </Col>
                    <span className={styles.Dash}>-</span>
                    <Col md={5} className={styles.DateCol}>
                      <DatePicker
                        id="limitEndAt"
                        className={styles.CampaignDatePicker}
                        showTime={{ format: 'HH:mm' }}
                        format="DD/MM/YYYY HH:mm"
                        value={toDatePickerValue(validation.values.limitEndAt)}
                        onChange={onChangeLimitEndDate}
                        needConfirm={false}
                        inputReadOnly
                        placeholder="Kết thúc vào"
                        disabledDate={disabledEndDate}
                        defaultPickerValue={toDatePickerValue(
                          validation.values.limitStartAt,
                        )}
                      />
                      {getFieldError('limitEndAt') && (
                        <FormFeedback className="d-block mt-2">
                          {getFieldError('limitEndAt')}
                        </FormFeedback>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Trạng thái</p>
                <div className={styles.OptionGroup}>
                  <label htmlFor="gift-status-hidden">
                    <Input
                      id="gift-status-hidden"
                      type="radio"
                      name="status"
                      value={GIFT_STATUS.HIDDEN}
                      checked={validation.values.status === GIFT_STATUS.HIDDEN}
                      onChange={onChangeStatus}
                    />
                    <span>Ẩn</span>
                  </label>
                  <label htmlFor="gift-status-public">
                    <Input
                      id="gift-status-public"
                      type="radio"
                      name="status"
                      value={GIFT_STATUS.PUBLIC}
                      checked={validation.values.status === GIFT_STATUS.PUBLIC}
                      onChange={onChangeStatus}
                    />
                    <span>Công khai</span>
                  </label>
                </div>
              </div>
            </Card>
            {/* Value & Inventory */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>Giá trị &amp; Tồn kho</h3>

              <Row className={styles.ValueGrid}>
                <Col md={6} className={styles.ValueCol}>
                  <div className={styles.FormField}>
                    <p className={styles.FormFieldTitle}>Giá đổi điểm</p>
                    <div className={styles.BcoinField}>
                      <AppInputNumberValidation
                        name="redeemPoints"
                        keyName="redeemPoints"
                        placeholder="0.00"
                        validation={validation}
                        inputClassName={styles.BcoinInput}
                        thousandSeparator=","
                        decimalSeparator="."
                      />
                      <span
                        className={classNames(
                          styles.BcoinSuffix,
                          getFieldError('redeemPoints') &&
                            styles.BcoinSuffixError,
                        )}
                      >
                        Xu
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className={styles.ValueGrid}>
                {!isVoucherGift && (
                  <Col md={6} className={styles.ValueCol}>
                    <div className={styles.FormField}>
                      <p className={styles.FormFieldTitle}>
                        Số lượng trong kho
                      </p>
                      <div className={styles.Stepper}>
                        <Button
                          isDefault
                          type="button"
                          classes={classNames(
                            styles.StepperBtn,
                            styles.StepperBtnDecrease,
                          )}
                          onClick={onAdjustStock(-1)}
                          disabled={
                            !validation.values.stockQuantity || isVoucherGift
                          }
                          aria-label="Giảm số lượng"
                        >
                          <i className="mdi mdi-minus" />
                        </Button>
                        <Input
                          type="number"
                          min={0}
                          max={999}
                          disabled={isVoucherGift}
                          className={styles.StepperInput}
                          value={validation.values.stockQuantity}
                          onChange={onChangeStockQuantity}
                          onKeyDown={onKeyDownIntegerOnly}
                          onFocus={(e) => e.target.select()}
                        />
                        <Button
                          isDefault
                          type="button"
                          classes={classNames(
                            styles.StepperBtn,
                            styles.StepperBtnIncrease,
                          )}
                          onClick={onAdjustStock(1)}
                          disabled={
                            isVoucherGift ||
                            validation.values.stockQuantity >= 999
                          }
                          aria-label="Tăng số lượng"
                        >
                          <PlusIcon className={styles.StepperPlusIcon} />
                        </Button>
                      </div>
                      {getFieldError('stockQuantity') && (
                        <FormFeedback className="d-block">
                          {getFieldError('stockQuantity')}
                        </FormFeedback>
                      )}
                    </div>
                  </Col>
                )}

                <Col md={6} className={styles.ValueCol}>
                  <div className={styles.FormField}>
                    <p className={styles.FormFieldTitle}>
                      Giới hạn đổi của một tài khoản
                    </p>
                    <div className={styles.Stepper}>
                      <Button
                        isDefault
                        type="button"
                        classes={classNames(
                          styles.StepperBtn,
                          styles.StepperBtnDecrease,
                        )}
                        onClick={onAdjustPerAccountLimit(-1)}
                        disabled={!validation.values.perAccountRedeemLimit}
                        aria-label="Giảm giới hạn"
                      >
                        <i className="mdi mdi-minus" />
                      </Button>
                      <Input
                        type="number"
                        min={0}
                        max={999}
                        className={styles.StepperInput}
                        value={validation.values.perAccountRedeemLimit}
                        onChange={onChangePerAccountLimit}
                        onKeyDown={onKeyDownIntegerOnly}
                        onFocus={(e) => e.target.select()}
                      />
                      <Button
                        isDefault
                        type="button"
                        classes={classNames(
                          styles.StepperBtn,
                          styles.StepperBtnIncrease,
                        )}
                        onClick={onAdjustPerAccountLimit(1)}
                        disabled={
                          validation.values.perAccountRedeemLimit >= 999
                        }
                        aria-label="Tăng giới hạn"
                      >
                        <PlusIcon className={styles.StepperPlusIcon} />
                      </Button>
                    </div>
                    {getFieldError('perAccountRedeemLimit') && (
                      <FormFeedback className="d-block">
                        {getFieldError('perAccountRedeemLimit')}
                      </FormFeedback>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
            {/* Upload img */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>
                Ảnh đại diện
                {getFieldError('images') && (
                  <FormFeedback className="d-block mt-2 fw-normal">
                    {getFieldError('images')}
                  </FormFeedback>
                )}
              </h3>

              <Loading loading={isLoadingUploadMultipleMedia} loaderOnPage />

              {validation.values.images.length > 0 && (
                <div className={styles.ImageButtonWrap}>
                  <Button
                    isDefault
                    classes={styles.ImageButtonCheckAll}
                    onClick={onSelectAllImages}
                  >
                    Chọn tất cả
                  </Button>
                  <Button
                    isDefault
                    classes={styles.ImageButtonUnCheck}
                    onClick={onClearSelectAllImages}
                    disabled={
                      !validation.values.images.some((item) => item.checked)
                    }
                  >
                    Bỏ chọn tất cả
                  </Button>
                  <Button
                    isDefault
                    classes={styles.ImageButtonRemove}
                    onClick={onDeleteSelectedImages}
                    disabled={
                      !validation.values.images.some((item) => item.checked)
                    }
                  >
                    Xóa
                  </Button>
                </div>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragImage}
                onDragStart={onDragStart}
                onDragCancel={onDragCancel}
              >
                <SortableContext
                  items={validation.values.images.map((item) => item.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className={styles.ImageList} {...getRootProps()}>
                    <div
                      className={classNames(
                        styles.DropImageLayout,
                        isDragAccept && styles.DropImageActive,
                      )}
                    >
                      <AddPictureIcon width={64} height={64} />
                      <span>THÊM ẢNH</span>
                    </div>

                    <input
                      {...getInputProps()}
                      ref={inputFileRef}
                      onChange={onChangeAddImages}
                    />

                    {validation.values.images.map((image) => (
                      <CardImage
                        key={image.id}
                        item={image}
                        onSelectImage={onSelectImage}
                        style={GIFT_IMAGE_THUMB_STYLE}
                      />
                    ))}

                    <Button
                      isDefault
                      type="button"
                      classes={styles.UploadImage}
                      onClick={onClickOpenFileInput}
                    >
                      <AddPictureIcon width={44} height={44} />
                      <span>THÊM ẢNH</span>
                    </Button>
                  </div>
                </SortableContext>

                <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
                  {activeDragImage ? (
                    <GiftImageDragPreview
                      item={activeDragImage}
                      style={GIFT_IMAGE_THUMB_STYLE}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Card>

            {/* detail content */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>Nội dung chi tiết</h3>

              {GIFT_DETAIL_FIELDS.map(({ name, label, placeholder }) => (
                <div key={name} className={styles.FormField}>
                  <p className={styles.FormFieldTitle}>{label}</p>
                  <AppInputValidation
                    type="textarea"
                    name={name}
                    keyName={name}
                    placeholder={placeholder}
                    validation={validation}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    limitCharacter={GIFT_DETAIL_MAX_LENGTH}
                    isShowCounter
                    isShowError={false}
                    inputClassName={styles.DetailTextarea}
                    style={{ minHeight: 84 }}
                  />
                </div>
              ))}
            </Card>
          </div>
        </div>
      </Container>

      <div className={styles.FormActions}>
        <Button type="button" color={themeColor.SECONDARY} onClick={onCancel}>
          Hủy
        </Button>
        <Button
          type="button"
          loading={isCreateGiftLoading}
          disabled={isCreateGiftLoading}
          onClick={onSaveGift}
        >
          Tạo
        </Button>
      </div>
    </div>
  );
}

ManagerGiftCreate.propTypes = {
  isLoadingUploadMultipleMedia: PropTypes.bool,
  isCreateGiftLoading: PropTypes.bool,
  actionUploadMultiMedia: PropTypes.func,
  actionCreateGift: PropTypes.func,
};

ManagerGiftCreate.defaultProps = {
  isLoadingUploadMultipleMedia: false,
  isCreateGiftLoading: false,
  actionUploadMultiMedia: INITIAL_VALUE.FUNCTION,
  actionCreateGift: INITIAL_VALUE.FUNCTION,
};

export default ManagerGiftCreate;
